import { useEffect, useState, useCallback } from "react";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import AgentsService from "../../services/AgentsService/AgentsService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { normalizeAgentProperties } from "../../utils/NormalizationUtil.ts";
import AddAgentPopup from "../Popup/AddAgentPopup.tsx";
import RemoveAgentPopup from "../Popup/RemoveAgentPopup.tsx";
import AgentsDataGrid from "../../components/DataTables/AgentsDataGrid.tsx";
import { Agent } from "../../types/agent.ts";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import ConfirmationModal from "../../components/Modals/ConfirmationModal.tsx";

const OfficesAndAgentsManagement = () => {
  const { jwt } = useAuth();
  const [allOffices, setAllOffices] = useState<{ [key: string]: string }>({});
  const [agentsList, setAgentsList] = useState<Agent[]>([]);
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const [selectedOffice, setSelectedOffice] = useState<string>("");
  const [showAddAgentPopup, setShowAddAgentPopup] = useState<boolean>(false);
  const [showRemoveAgentPopup, setShowRemoveAgentPopup] =
    useState<boolean>(false);
  const [subAgentsIds, setSubAgentsIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalWarning, setConfirmModalWarning] =
    useState<React.ReactNode>(null);

  const [pendingAgents, setPendingAgents] = useState<string[] | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isOfficeSelected = selectedOffice !== "";

  const displayAlert = useCallback((message: string, type: FadeAlertType) => {
    setAlert(message);
    setAlertType(type);
  }, []);

  const normalizeName = (name: string) =>
    name.trim().replace(/\s+/g, " ").toLocaleLowerCase("bg");

  const fetchOfficesData = useCallback(async () => {
    try {
      const officesAll = await InsuranceService.getAllOffices(jwt, false);
      setAllOffices(officesAll as { [key: string]: string });
    } catch (err: any) {
      displayAlert(err.message, FadeAlertType.error);
    }
  }, [jwt, displayAlert]);

  const fetchAgentsForOffice = useCallback(
    async (officeId: string) => {
      try {
        setLoading(true);
        const ids = await AgentsService.getSubAgentsIdsByAgentId(jwt, officeId);

        const validIds = (ids as string[]).filter(
          (id) => typeof id === "string" && id.trim() !== ""
        );

        setSubAgentsIds(validIds);
        const subAgents: Agent[] = await Promise.all(
          validIds.map(async (agentId): Promise<Agent> => {
            const normalizedAgent = normalizeAgentProperties(
              (await AgentsService.getAgentById(jwt, agentId)) as object
            );
            normalizedAgent.isActive = normalizedAgent.isActive === 1;
            return normalizedAgent;
          })
        );
        setAgentsList(subAgents);
      } catch (err: any) {
        displayAlert(err.message, FadeAlertType.error);
      } finally {
        setLoading(false);
      }
    },
    [jwt, displayAlert]
  );

  useEffect(() => {
    fetchOfficesData();
  }, [fetchOfficesData]);

  const handleOfficeSelectChange = useCallback(
    async (value: string) => {
      setSelectedOffice(value);
      if (value) await fetchAgentsForOffice(value);
    },
    [fetchAgentsForOffice]
  );

  const handleAddAgent = useCallback(
    async (selectedAgents: string[]) => {
      try {
        if (selectedOffice) {
          await AgentsService.saveAgentsToOffice(
            jwt,
            selectedOffice,
            selectedAgents
          );
          displayAlert("Успешен запис", FadeAlertType.success);
          await fetchAgentsForOffice(selectedOffice);
        } else {
          displayAlert("Моля, изберете офис", FadeAlertType.error);
        }
      } catch (err: any) {
        displayAlert(err.message, FadeAlertType.error);
      }
      setShowAddAgentPopup(false);
    },
    [jwt, selectedOffice, fetchAgentsForOffice, displayAlert]
  );

  const handleRemoveAgent = useCallback(
    async (updatedAgents: string[]) => {
      if (!selectedOffice) {
        displayAlert("Моля, изберете офис", FadeAlertType.error);
        return;
      }

      try {
        setLoading(true);

        // Identify removed agents
        const removedIds = subAgentsIds.filter(
          (id) => !updatedAgents.includes(id)
        );

        if (removedIds.length === 0) {
          setShowRemoveAgentPopup(false);
          return;
        }

        // Fetch agents across all offices
        const allAgentsResponse = await AgentsService.getAgentsList(jwt);

        const allAgents = allAgentsResponse.map((agent) =>
          normalizeAgentProperties(agent)
        );

        // Check each removed agent
        const removedAgents = removedIds.map((id) => {
          const agent = agentsList.find(
            (a) => String(a.agentID) === String(id)
          );

          const existsElsewhere = agent
            ? allAgents.some(
                (other) =>
                  String(other.agentID) !== String(id) &&
                  String(other.officeId) !== String(selectedOffice) &&
                  normalizeName(other.agentName) ===
                    normalizeName(agent.agentName)
              )
            : false;

          return {
            name: agent?.agentName ?? id,
            existsElsewhere,
          };
        });

        const agentsNotFoundElsewhere = removedAgents.filter(
          (agent) => !agent.existsElsewhere
        );

        setConfirmModalWarning(
          agentsNotFoundElsewhere.length > 0 ? (
            <>
              Следните агенти не присъстват в друг офис и след премахването им
              няма да бъдат асоциирани с нито един офис:
              <ul className="mt-2 list-disc pl-5">
                {agentsNotFoundElsewhere.map((agent, index) => (
                  <li key={index}>
                    <strong>{agent.name}</strong>
                  </li>
                ))}
              </ul>
            </>
          ) : null
        );

        setPendingAgents(updatedAgents);
        setShowRemoveAgentPopup(false);
        setShowConfirmModal(true);
      } catch (err: any) {
        displayAlert(err.message, FadeAlertType.error);
      } finally {
        setLoading(false);
      }
    },
    [jwt, selectedOffice, subAgentsIds, agentsList, allOffices, displayAlert]
  );

  const executeRemoveAgent = useCallback(async () => {
    if (!selectedOffice || !pendingAgents || isSaving) return;

    try {
      setIsSaving(true);

      await AgentsService.saveAgentsToOffice(
        jwt,
        selectedOffice,
        pendingAgents
      );

      displayAlert("Успешен запис", FadeAlertType.success);

      await fetchAgentsForOffice(selectedOffice);
    } catch (err: any) {
      displayAlert(err.message, FadeAlertType.error);
    } finally {
      setIsSaving(false);
      setShowConfirmModal(false);
      setPendingAgents(null);
    }
  }, [
    jwt,
    selectedOffice,
    pendingAgents,
    isSaving,
    displayAlert,
    fetchAgentsForOffice,
  ]);

  return (
    <>
      <Breadcrumb pageName="Управление на офиси и агенти" />
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark mt-2 rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Управление на офиси и агенти" />

        <div className="p-3">
          {/* Office select + action buttons in one row */}
          <div className="mb-2 flex items-end gap-2">
            <div className="flex-1">
              <label className="mb-1 ml-1 block text-xs font-medium text-black dark:text-white">
                Офис
              </label>
              <SelectGroup
                value={selectedOffice}
                placeholder="Изберете офис"
                options={allOffices}
                onChange={handleOfficeSelectChange}
              />
            </div>
            <button
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition ${
                isOfficeSelected
                  ? "bg-primary text-white hover:bg-opacity-90"
                  : "cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700"
              }`}
              onClick={() => setShowAddAgentPopup(true)}
              disabled={!isOfficeSelected}
            >
              <i className="fa-kit fa-solid-plus-user" />
              Добавяне
            </button>
            <button
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition ${
                isOfficeSelected
                  ? "bg-primary text-white hover:bg-opacity-90"
                  : "cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700"
              }`}
              onClick={() => setShowRemoveAgentPopup(true)}
              disabled={!isOfficeSelected}
            >
              <i className="fa-kit fa-solid-minus-user" />
              Премахване
            </button>
          </div>

          <AgentsDataGrid agentsList={agentsList} isLoading={loading} />
        </div>

        {showAddAgentPopup && (
          <AddAgentPopup
            officeName={allOffices[selectedOffice]}
            agentsIds={subAgentsIds}
            onClose={() => setShowAddAgentPopup(false)}
            onSave={handleAddAgent}
          />
        )}
        {showRemoveAgentPopup && (
          <RemoveAgentPopup
            officeName={allOffices[selectedOffice]}
            agentsIds={subAgentsIds}
            onClose={() => setShowRemoveAgentPopup(false)}
            onSave={handleRemoveAgent}
          />
        )}
        <ConfirmationModal
          isOpen={showConfirmModal}
          warning={confirmModalWarning}
          onConfirm={executeRemoveAgent}
          onCancel={() => {
            if (isSaving) return;

            setShowConfirmModal(false);
            setPendingAgents(null);
          }}
        />
      </div>
    </>
  );
};

export default OfficesAndAgentsManagement;
